/**
 * Helper functions for CTS (Customer Transaction Sheet) operations
 * Mirrors Flutter cts_helper.dart logic exactly
 * @author MFG Invest Team
 */

import { checkCtsMustSign as apiCheckMustSign, viewCtsToday as apiViewCtsToday } from './apiClient';
import AppConstants from './appConstants';

/**
 * Result of checking if user must sign CTS
 */
export class CtsCheckResult {
  constructor(mustSign, ctsData, error) {
    this.mustSign = mustSign;
    this.ctsData = ctsData;
    this.error = error;
  }
}

/**
 * CTS Status enum
 */
export const CtsStatus = {
  NEEDS_CREATION: 'NEEDS_CREATION', // No CTS exists for today
  NEEDS_SIGNATURE: 'NEEDS_SIGNATURE', // CTS exists but not signed
  SIGNED: 'SIGNED', // CTS is signed and ready
};

/**
 * Check if user must sign CTS for the given amount/currency.
 * Matches Flutter CtsHelper.checkMustSign() exactly
 * @param {{ amount?: number, currencyId?: number }} params
 * @returns {Promise<CtsCheckResult>}
 */
export async function checkMustSign(params = {}) {
  const result = await apiCheckMustSign(params);
  
  // Matches Flutter lines 26-28
  if (result.error) {
    return new CtsCheckResult(false, null, true);
  }
  
  // Matches Flutter lines 30-48
  if (result.mustSign !== undefined && result.mustSign) {
    let ctsData = null;
    if (result.ctsData && result.ctsData.ctsId != null) {
      ctsData = {
        ctsId: result.ctsData.ctsId,
        status: result.ctsData.status,
        hasSignature: result.ctsData.hasSignature,
        // Add isSigned property for convenience
        isSigned: result.ctsData.status === 1 && result.ctsData.hasSignature === true,
      };
    }
    return new CtsCheckResult(true, ctsData, false);
  }
  
  // Matches Flutter line 49-50 (CODE == 206)
  // Matches Flutter line 51-53 (other errors)
  return new CtsCheckResult(false, null, false);
}

/**
 * Get today's CTS data.
 * Matches Flutter CtsHelper.getTodayCts() and CtsDailyModel structure.
 * @returns {Promise<{ ctsId?: number, ctsDate?: string, sourceOfFunds?: number, totalAmount?: number, status?: number, hasAttachment?: boolean, hasSignature?: boolean, attachmentId?: number, signatureId?: number, signatureUrl?: string, createdAt?: Date, updatedAt?: Date, isSigned?: boolean } | null>}
 */
export async function getTodayCts() {
  const response = await apiViewCtsToday();
  
  if (!response) {
    return null;
  }
  
  // When CODE == 200 we have a full CTS record for today.
  // When CODE == 206 we might still get default values like SOURCE_OF_FUNDS
  // even if the CTS was not formally created yet.
  // The apiViewCtsToday already handles CODE 200 and 206, so we just return the data
  if (response.ctsId == null && response.sourceOfFunds == null) {
    return null;
  }
  
  // Calculate isSigned: status == 1 && hasSignature == true
  const isSigned = response.status === 1 && response.hasSignature === true;
  
  return {
    ctsId: response.ctsId,
    ctsDate: response.ctsDate,
    sourceOfFunds: response.sourceOfFunds,
    totalAmount: response.totalAmount,
    status: response.status,
    hasAttachment: response.hasAttachment,
    hasSignature: response.hasSignature,
    attachmentId: response.attachmentId,
    signatureId: response.signatureId,
    signatureUrl: response.signatureUrl || response.signatureImageUrl,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    isSigned: isSigned,
  };
}

/**
 * Check CTS status and determine what screen to show.
 * Matches Flutter CtsHelper.checkCtsStatus()
 * @returns {Promise<'NEEDS_CREATION'|'NEEDS_SIGNATURE'|'SIGNED'>}
 */
export async function checkCtsStatus() {
  const ctsData = await getTodayCts();
  
  // NOTE:
  // - If there is no CTS at all OR CTS_ID is null, we still need to CREATE it,
  //   even if the API already returned a default SOURCE_OF_FUNDS (CODE 206).
  // - In that case we will pass the default SOURCE_OF_FUNDS to the creation dialog
  //   but the status from the perspective of the flow is still "NEEDS_CREATION".
  if (ctsData == null || ctsData.ctsId == null) {
    // No CTS exists for today (or only defaults returned) - need to create
    return CtsStatus.NEEDS_CREATION;
  } else if (!ctsData.isSigned) {
    // CTS exists but not signed - need to sign
    return CtsStatus.NEEDS_SIGNATURE;
  } else {
    // CTS is signed - can proceed with cash-in
    return CtsStatus.SIGNED;
  }
}

